import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
  Autocomplete,
  Box,
  IconButton,
  InputAdornment,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { useMemo } from "react";
import { Controller } from "react-hook-form";
import { AuthStateAudienceSelection } from "./AutomatedNotificationsForm";
import { MAX_APP_CLIP_NOTIFICATION_DELAY_MINUTES } from "./delayOptions";

type AutomatedNotificationItemProps = {
  index: number;
  groupName: string;
  formName: string;
  register: any;
  errors: any;
  control: any;
  watch: any;
  onDelete: () => void;
  delayOptions: { value: number; label: string }[];
  isAppClip?: boolean;
  showLandingPath: boolean;
};

const TITLE_MAX = 50;
const DESC_MAX = 150;

const AutomatedNotificationItem = ({
  index,
  groupName,
  formName,
  register,
  errors,
  control,
  watch,
  onDelete,
  delayOptions,
  isAppClip = false,
  showLandingPath,
}: AutomatedNotificationItemProps) => {
  const maxDelayMinutes = isAppClip
    ? MAX_APP_CLIP_NOTIFICATION_DELAY_MINUTES
    : undefined;

  const titleValue = watch(`${formName}.title`) || "";
  const descValue = watch(`${formName}.message`) || "";

  const audienceOptions = useMemo(() => {
    return Object.values(AuthStateAudienceSelection).map((option) => ({
      value: option,
      label: option,
    }));
  }, []);

  const formatDelayLabel = (minutes: number | string) => {
    if (typeof minutes === "string" && minutes.trim() === "") {
      return "Please set a delay";
    }
    minutes = Number(minutes);
    if (Number.isNaN(minutes)) {
      return "Please set a delay";
    }
    if (minutes < 60) {
      return `${minutes} minute${minutes !== 1 ? "s" : ""} later`;
    } else if (minutes === 60) {
      return "1 hour later";
    } else if (minutes < 1440) {
      const hours = minutes / 60;
      return `${hours} hour${hours !== 1 ? "s" : ""} later`;
    } else if (minutes === 1440) {
      return "1 day later";
    } else {
      const days = minutes / 1440;
      return `${days} day${days !== 1 ? "s" : ""} later`;
    }
  };

  return (
    <Box
      sx={{
        border: "1.5px solid",
        borderColor: "primary.light",
        borderRadius: 2,
        p: 2,
        mb: 2,
        background: "#fff",
        position: "relative",
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        spacing={2}
        mb={3}
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={2}
          sx={{
            opacity: !watch(formName + ".isEnabled") ? "70%" : undefined,
            pointerEvents: !watch(formName + ".isEnabled") ? "none" : undefined,
          }}
        >
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: "50%",
              backgroundColor: "primary.main",
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: 600,
              fontSize: 18,
            }}
          >
            {index + 1}
          </Box>
          <Typography variant="subtitle2" fontWeight={600}>
            {groupName} - Push Notification
          </Typography>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Controller
            render={(props) => (
              <Switch
                checked={props.field.value}
                onChange={(_, data) => props.field.onChange(data)}
              />
            )}
            name={formName + ".isEnabled"}
            control={control}
          />
          <IconButton color="error" onClick={onDelete}>
            <DeleteOutlineIcon />
          </IconButton>
        </Stack>
      </Stack>
      <Stack
        spacing={2}
        sx={{
          opacity: !watch(formName + ".isEnabled") ? "70%" : undefined,
          pointerEvents: !watch(formName + ".isEnabled") ? "none" : undefined,
        }}
      >
        <TextField
          {...register(`${formName}.title`)}
          error={!!errors?.title}
          helperText={errors?.title?.message}
          label="Notification Title"
          placeholder="Enter Notification Title..."
          fullWidth
          inputProps={{ maxLength: TITLE_MAX }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {titleValue.length}/{TITLE_MAX}
              </InputAdornment>
            ),
          }}
        />
        <TextField
          {...register(`${formName}.message`)}
          error={!!errors?.message}
          helperText={errors?.message?.message}
          label="Notification Description"
          placeholder="Compose your notification message here..."
          fullWidth
          multiline
          rows={2}
          inputProps={{ maxLength: DESC_MAX }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {descValue.length}/{DESC_MAX}
              </InputAdornment>
            ),
          }}
        />
        <Controller
          name={`${formName}.delayMinutes`}
          control={control}
          render={({ field }) => {
            const currentValue = field.value;
            const selectedOption = delayOptions.find(
              (option) => option.value === currentValue
            );
            const isCustom = !selectedOption;

            return (
              <Autocomplete
                options={delayOptions}
                getOptionLabel={(option) => {
                  if (typeof option === "string") {
                    return option;
                  }
                  return option.label;
                }}
                value={selectedOption || null}
                onChange={(_, newValue) => {
                  if (newValue && typeof newValue === "object") {
                    field.onChange(newValue.value);
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Reminder Delay"
                    error={!!errors?.delayMinutes}
                    helperText={
                      errors?.delayMinutes?.message ||
                      (isCustom ? formatDelayLabel(currentValue) : undefined)
                    }
                    placeholder="Select delay or enter minutes..."
                    required
                    onChange={(e) => {
                      // Handle direct numeric input
                      const value = parseInt(e.target.value);
                      if (!isNaN(value) && value >= 1) {
                        const maxValue = isAppClip ? 480 : 262980;
                        const clampedValue = Math.min(value, maxValue);
                        field.onChange(clampedValue);
                      }
                    }}
                  />
                )}
                freeSolo
                inputValue={
                  selectedOption
                    ? selectedOption.label
                    : isCustom
                    ? currentValue.toString()
                    : ""
                }
                onInputChange={(_, newInputValue) => {
                  // Handle free text input
                  if (newInputValue === "") {
                    // Allow empty input
                    field.onChange("");
                  } else {
                    // if newInputValue contains non numeric characters, return (as it's an autocomplete option)
                    if (isNaN(Number(newInputValue))) {
                      return;
                    }
                    const value = parseInt(newInputValue);
                    if (!isNaN(value) && value >= 1) {
                      field.onChange(value);
                    }
                  }
                }}
                filterOptions={(options, { inputValue }) => {
                  // Show all options plus any numeric input
                  const numericValue = parseInt(inputValue);
                  if (!isNaN(numericValue) && numericValue >= 1) {
                    const customMinutesOption = {
                      value: numericValue,
                      label: `${numericValue} minutes later`,
                    };
                    const maxHours = maxDelayMinutes
                      ? Math.floor(maxDelayMinutes / 60)
                      : 24;
                    const customHoursOptions =
                      numericValue <= maxHours
                        ? [
                            {
                              value: numericValue * 60,
                              label: `${numericValue} hours later`,
                            },
                          ]
                        : [];
                    const maxDays = maxDelayMinutes
                      ? Math.floor(maxDelayMinutes / 1440)
                      : 100;
                    const customDaysOptions =
                      numericValue <= maxDays
                        ? [
                            {
                              value: numericValue * 1440,
                              label: `${numericValue} days later`,
                            },
                          ]
                        : [];

                    return [
                      customMinutesOption,
                      ...customHoursOptions,
                      ...customDaysOptions,
                      ...options,
                    ].filter(Boolean);
                  }
                  return options;
                }}
                clearOnBlur={false}
                selectOnFocus
                clearOnEscape
              />
            );
          }}
        />
        {showLandingPath && (
          <TextField
            {...register(formName + ".landingPath")}
            error={!!errors?.landingPath?.message}
            helperText={errors?.landingPath?.message}
            fullWidth
            autoComplete="off"
            sx={{
              flexGrow: 1,
            }}
            margin="dense"
            id="outlined-basic"
            label={"Notification Landing Path"}
            variant="outlined"
          />
        )}
        {/* Audience selection */}
        <Controller
          name={`${formName}.audience`}
          control={control}
          defaultValue={AuthStateAudienceSelection.EVERYONE}
          render={({ field }) => (
            <Autocomplete
              options={audienceOptions}
              getOptionLabel={(option) => {
                if (typeof option === "string") {
                  return option;
                }
                return option.label;
              }}
              value={
                field.value
                  ? {
                      value: field.value,
                      label: field.value,
                    }
                  : audienceOptions[0]
              }
              onChange={(_, newValue) => {
                if (newValue && typeof newValue === "object") {
                  field.onChange(newValue.value);
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Audience"
                  error={!!errors?.audience}
                  helperText={errors?.audience?.message}
                  placeholder="Select audience..."
                />
              )}
              disableClearable
              isOptionEqualToValue={(option, value) =>
                option.value === value.value
              }
            />
          )}
        />
      </Stack>
    </Box>
  );
};

export default AutomatedNotificationItem;
